import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MemberDialog, OwnerDialog } from "../components/team/TeamDialogs";
import { useTeamDashboard } from "../hooks/useTeam";
import { euro } from "../lib/team";
import type { TeamMemberRow, TeamOwner } from "../lib/team";
import "../styles/team.css";

type Segment = "team" | "owners";
type ActiveFilter = "active" | "inactive";

export default function TeamPage() {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useTeamDashboard();
  const [segment, setSegment] = useState<Segment>("team");
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>("active");
  const [query, setQuery] = useState("");
  const [memberDialog, setMemberDialog] = useState<"new" | TeamMemberRow | null>(null);
  const [ownerDialog, setOwnerDialog] = useState<"new" | TeamOwner | null>(null);
  const ownerIds = useMemo(() => new Set((data?.owners ?? []).map((owner) => owner.id)), [data?.owners]);

  const members = useMemo(() => (data?.members ?? [])
    .filter((member) => member.role !== "inhaber" && !ownerIds.has(member.id))
    .filter((member) => member.active === (activeFilter === "active"))
    .filter((member) => `${member.name} ${member.phone} ${member.email}`.toLocaleLowerCase("de")
      .includes(query.trim().toLocaleLowerCase("de")))
    .sort((a, b) => {
      const offeneAktionenA = a.einladungen_offen + a.bewerbungen_offen + a.arbeitszeiten_offen;
      const offeneAktionenB = b.einladungen_offen + b.bewerbungen_offen + b.arbeitszeiten_offen;
      return offeneAktionenB - offeneAktionenA
        || b.offener_saldo - a.offener_saldo
        || a.name.localeCompare(b.name, "de");
    }),
  [activeFilter, data?.members, ownerIds, query]);

  const owners = (data?.owners ?? []).filter((owner) => owner.active === (activeFilter === "active"));

  return (
    <div className="team-page">
      <header className="team-page-head">
        <div><p className="team-kicker">Betrieb</p><h1>Team</h1><p>Einsatz-Antworten, Arbeitszeiten und Lohnkonten im Überblick.</p></div>
        <button className="team-button primary" type="button" onClick={() => segment === "team" ? setMemberDialog("new") : setOwnerDialog("new")}>
          <span aria-hidden="true">＋</span> {segment === "team" ? "Neuer Helfer" : "Neuer Inhaber"}
        </button>
      </header>

      <nav className="team-segments" aria-label="Teamansicht">
        <button className={segment === "team" ? "active" : ""} onClick={() => setSegment("team")}>Team</button>
        <button className={segment === "owners" ? "active" : ""} onClick={() => setSegment("owners")}>Inhaber</button>
      </nav>

      {segment === "team" && <Kpis data={data} />}
      {segment === "team" && <EinsatzKpis data={data} />}

      <section className="team-panel">
        <div className="team-toolbar">
          <div className="team-status-tabs">
            <button className={activeFilter === "active" ? "active" : ""} onClick={() => setActiveFilter("active")}>Aktiv</button>
            <button className={activeFilter === "inactive" ? "active" : ""} onClick={() => setActiveFilter("inactive")}>Inaktiv</button>
          </div>
          {segment === "team" && <label className="team-search"><span aria-hidden="true">⌕</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Team durchsuchen …" /></label>}
          <span className="team-result-count">{segment === "team" ? members.length : owners.length} Einträge</span>
        </div>

        {isLoading && <State title="Team wird geladen …" />}
        {isError && <State title="Teamdaten konnten nicht geladen werden." action={() => refetch()} />}
        {!isLoading && !isError && segment === "team" && (
          <div className="team-table-wrap">
            <table className="team-table">
              <thead><tr><th>Name</th><th>Job-Antworten</th><th>Zeiten offen</th><th>Offener Saldo</th><th>Verdient {new Date().getFullYear()}</th><th>Stundenlohn</th><th>Status</th></tr></thead>
              <tbody>{members.map((member) => (
                <tr key={member.id} tabIndex={0} onClick={() => navigate(`/team/${member.id}`)} onKeyDown={(event) => event.key === "Enter" && navigate(`/team/${member.id}`)}>
                  <td><div className="team-person"><Avatar name={member.name} /><span><strong>{member.name}</strong><small>{member.email || member.phone || "Kein Kontakt hinterlegt"}</small></span></div></td>
                  <td><div className="team-status-counts"><span className={member.einladungen_offen || member.bewerbungen_offen ? "needs-action" : ""}>{member.einladungen_offen + member.bewerbungen_offen} offen</span><small>{member.einsaetze_zugesagt} zugesagt · {member.einsaetze_abgesagt} abgesagt</small></div></td>
                  <td>{member.arbeitszeiten_offen ? <span className="team-pill warning">{member.arbeitszeiten_offen} prüfen</span> : <span className="team-pill muted">0</span>}</td>
                  <td className={member.offener_saldo > 0 ? "money-positive" : ""}>{euro(member.offener_saldo)}</td>
                  <td>{euro(member.verdient_jahr)}</td><td>{euro(member.hourly_rate)}</td>
                  <td><span className={`team-pill ${member.active ? "success" : "muted"}`}>{member.active ? "Aktiv" : "Inaktiv"}</span></td>
                </tr>
              ))}</tbody>
            </table>
            {/* Am Telefon eine Liste statt der Tabelle (30.10.2026): die Tabelle
                versteckte dort ausgerechnet den offenen Saldo und lief seitlich aus. */}
            <ul className="team-liste">
              {members.map((member) => {
                const antwortenOffen = member.einladungen_offen + member.bewerbungen_offen;
                return (
                  <li key={member.id}>
                    <button type="button" onClick={() => navigate(`/team/${member.id}`)}>
                      <Avatar name={member.name} />
                      <span className="team-liste-text">
                        <strong>{member.name}</strong>
                        <small>
                          {antwortenOffen > 0 && <em className="status-warning">{antwortenOffen} Antwort offen</em>}
                          {member.arbeitszeiten_offen > 0 && <em className="status-warning">{member.arbeitszeiten_offen} Zeiten prüfen</em>}
                          <span>{member.einsaetze_zugesagt} zugesagt · {euro(member.hourly_rate)}/Std.</span>
                        </small>
                      </span>
                      <span className="team-liste-saldo">
                        <strong className={member.offener_saldo > 0 ? "money-positive" : ""}>{euro(member.offener_saldo)}</strong>
                        <small>offen</small>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {!members.length && <State title={query ? "Keine passenden Helfer gefunden." : `Keine ${activeFilter === "active" ? "aktiven" : "inaktiven"} Helfer.`} />}
          </div>
        )}
        {!isLoading && !isError && segment === "owners" && (
          <div className="team-owner-grid">
            {owners.map((owner) => <button type="button" className="team-owner-card" key={owner.id} onClick={() => navigate(`/team/owner/${owner.id}`)}><Avatar name={owner.name} /><span><strong>{owner.name}</strong><small>{owner.email || owner.phone || "Keine Kontaktdaten"}</small></span><span className="team-pill info">Inhaber</span></button>)}
            {!owners.length && <State title={`Keine ${activeFilter === "active" ? "aktiven" : "inaktiven"} Inhaber.`} />}
          </div>
        )}
      </section>

      {memberDialog && <MemberDialog member={memberDialog === "new" ? undefined : memberDialog} onClose={() => setMemberDialog(null)} />}
      {ownerDialog && <OwnerDialog owner={ownerDialog === "new" ? undefined : ownerDialog} onClose={() => setOwnerDialog(null)} />}
    </div>
  );
}

function Kpis({ data }: { data: ReturnType<typeof useTeamDashboard>["data"] }) {
  const summary = data?.summary;
  return <div className="team-kpi-grid"><Kpi label="Offen gesamt" value={euro(summary?.total_saldo_offen_active ?? 0)} tone="green" /><Kpi label="Helfer mit offenem Saldo" value={String(summary?.members_with_open_balance ?? 0)} /><Kpi label={`Verdient ${new Date().getFullYear()}`} value={euro(summary?.earned_current_year ?? 0)} /><Kpi label="Aktive Helfer" value={String(summary?.active_member_count ?? 0)} /></div>;
}

function EinsatzKpis({ data }: { data: ReturnType<typeof useTeamDashboard>["data"] }) {
  const summary = data?.summary;
  return (
    <section className="team-dispatch-overview" aria-label="Einsatz-Antworten und Arbeitszeiten">
      <header><div><span className="team-section-label">Personaleinsatz</span><h2>Antworten &amp; Arbeitszeiten</h2></div><p>Zusage vor dem Job, Arbeitszeit danach.</p></header>
      <div className="team-kpi-grid">
        <Kpi label="Einladungen ohne Antwort" value={String(summary?.pending_invitation_count ?? 0)} tone={(summary?.pending_invitation_count ?? 0) > 0 ? "amber" : undefined} />
        <Kpi label="Offene Bewerbungen" value={String(summary?.pending_application_count ?? 0)} tone={(summary?.pending_application_count ?? 0) > 0 ? "amber" : undefined} />
        <Kpi label="Kommend zugesagt" value={String(summary?.accepted_upcoming_count ?? 0)} tone="green" />
        <Kpi label="Arbeitszeiten zu prüfen" value={String(summary?.open_work_entry_count ?? 0)} tone={(summary?.open_work_entry_count ?? 0) > 0 ? "amber" : undefined} />
      </div>
    </section>
  );
}

function Kpi({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return <article className="team-kpi"><span>{label}</span><strong className={tone === "green" ? "money-positive" : tone === "amber" ? "status-warning" : ""}>{value}</strong></article>;
}

export function Avatar({ name }: { name: string }) {
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toLocaleUpperCase("de");
  return <span className="team-avatar" aria-hidden="true">{initials || "?"}</span>;
}

function State({ title, action }: { title: string; action?: () => void }) {
  return <div className="team-state"><p>{title}</p>{action && <button type="button" className="team-button secondary" onClick={action}>Erneut versuchen</button>}</div>;
}
